/**
 * @file PlanDetailsList.js
 * @description Component that displays a list of Plan Details with search, sort, and pagination functionality.
 */
import React, { useState, useEffect, useContext } from 'react';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import Toast from '../ui/toasts/Toast';
import PaginationComponent from '../helpers/PaginationComponent';
import {
    fetchPlanDetails,
    deletePlanDetailById,
} from '../../services/systemService';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useNavigate } from 'react-router';
import { useAuthContext } from '../../store/AuthContext';
import { SecurityAttributes, LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import styles from './PlanDetails.module.scss';
import './PlanDetailsList.scss';
import '../page-list/PageList.css';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const initialSortConfig = {
    key: 'planType',
    direction: SortDirections.ASCENDING,
};

const PlanDetailsList = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { showModal } = useContext(ModalContext);
    const [planDetails, setPlanDetails] = useState([]);
    const [sortConfig, setSortConfig] = useState(initialSortConfig);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const { hasSecurityAttribute } = useAuthContext();
    const userHasPermissionToEdit = hasSecurityAttribute(
        [SecurityAttributes.PlanDetailFullAccess]
    );
    const pageSize = 10;

    const handleSortPlanDetailPlanType = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'planType',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'planType',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handleSortPlanDetailCategory = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'category',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'category',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handleSortPlanDetailName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'name',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'name',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handleSortPlanDetailCode = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'code',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'code',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handlerRowClicked = planDetailId => {
        setRowsClicked(prevSelection =>
            prevSelection.includes(planDetailId)
                ? prevSelection.filter(id => id !== planDetailId)
                : [...prevSelection, planDetailId],
        );
    };

    const handleDeletePlanDetails = async () => {
        try {
            await Promise.all(
                rowsClicked.map(planDetailsId =>
                    deletePlanDetailById(planDetailsId),
                ),
            );
            Toast.success(
                `${rowsClicked.length} Plan Details deleted successfully`,
            );
            setSortConfig(initialSortConfig);
            setIsLoading(true);
            setSearchTerm('');
            setRowsClicked([]);
            setCurrentPage(1);
        } catch (error) {
            console.error('Failed to delete Plan Details.', error);
            Toast.error(getText('msg_06729cc22465'));
        }
    };

    const openDeleteConfirmationModal = () => {
        if (!rowsClicked.length) {
            Toast.error(getText('msg_c8ccc5b97e0d'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            onYes: handleDeletePlanDetails,
            onNo: () => { },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Plan details delete clicked`);
    };

    const loadPlanDetails = async (isSearching = false) => {
        try {
            if (isSearching) setCurrentPage(1);
            const fetchedPlanDetails = await fetchPlanDetails(
                currentPage,
                pageSize,
                searchTerm,
                sortConfig.key,
                sortConfig.direction,
            );
            const { systemPlanDetails, pageTotal } = fetchedPlanDetails;
            setTotalPages(pageTotal);
            setPlanDetails(
                Array.isArray(systemPlanDetails) ? systemPlanDetails : [],
            );
            setInitialLoadComplete(true);
        } catch (error) {
            console.error('Failed to get Plan Details.', error);
            Toast.error(getText('msg_ecbd126780f2'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    useEffect(() => {
        loadPlanDetails();
    }, [currentPage, sortConfig]);

    useEffect(() => {
        if (!initialLoadComplete) return;
        const timeoutId = setTimeout(() => {
            loadPlanDetails(true);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content list-plan-details">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start d-flex align-items-center">
                            <div>
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_49f6b730623e')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {userHasPermissionToEdit
                                        ? 'Add new plan details or click the details icon to edit existing ones on the list.'
                                        : 'These details will only be available for clients to update when building content.'}
                                </p>
                            </div>
                        </div>
                        {userHasPermissionToEdit && (
                            <div className="float-end d-flex align-items-center">
                                <span
                                    type="button"
                                    onClick={openDeleteConfirmationModal}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    onClick={() =>
                                        navigate(`/plan-details/add`)
                                    }
                                    type="button"
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={styles.searchWrapper}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder={getText('msg_bce06414177f')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className={styles.searchIcon}>
                            <i className="bi bi-search"></i>
                        </span>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {userHasPermissionToEdit && (
                                        <th className="text-center plan-details-list-fixed-width">
                                            <svg
                                                width="19"
                                                height="19"
                                                viewBox="0 0 19 19"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5.2 9.3H12.8"
                                                    stroke="#0070FF"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <rect
                                                    x="0.5"
                                                    y="0.5"
                                                    width="17"
                                                    height="17"
                                                    rx="4"
                                                    stroke="#0070FF"
                                                />
                                            </svg>
                                        </th>)}
                                    <th>
                                        <span className="table-column-header-text bold-font">
                                            {getText('msg_45061ce8ef30')}
                                        </span>
                                        <span
                                            onClick={
                                                handleSortPlanDetailPlanType
                                            }
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'planType' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_a3c686e711e4')}
                                        </span>
                                        <span
                                            onClick={
                                                handleSortPlanDetailCategory
                                            }
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'category' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_ecdda59aea5e')}
                                        </span>
                                        <span
                                            onClick={handleSortPlanDetailName}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_adac69379a62')}
                                        </span>
                                        <span
                                            onClick={handleSortPlanDetailCode}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'code' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {planDetails?.map(planDetail => (
                                    <tr
                                        key={planDetail.id}
                                        className={`${rowsClicked.includes(planDetail.id)
                                            ? 'clicked-class'
                                            : ''
                                            }`}
                                    >
                                        {userHasPermissionToEdit && (
                                            <td className="text-center">

                                                <input
                                                    id={planDetail.id}
                                                    checked={rowsClicked.includes(
                                                        planDetail.id,
                                                    )}
                                                    onChange={() =>
                                                        handlerRowClicked(
                                                            planDetail.id,
                                                        )
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>
                                        )}
                                        <td className="titleCell"
                                            onClick={() =>
                                                navigate(
                                                    `/plan-details/edit/${planDetail.id}`,
                                                )
                                            }
                                        >
                                            {planDetail.planType}
                                        </td>
                                        <td className="subtitleCell">
                                            {planDetail.category}
                                        </td>
                                        <td className="subtitleCell">
                                            {planDetail.name}
                                        </td>
                                        <td className="subtitleCell">
                                            {planDetail.code}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {planDetails.length < 1 && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_985f62563741')}
                                description={getText('msg_a7493557a40f')}
                            />
                        )}
                        {totalPages > 1 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanDetailsList;
